/* SIM-16: "Show, for any computed cell, how the value was obtained."
 *
 * The panel displays the inputs and the RULE - it never recomputes, so what
 * the planner reads here is provably the arithmetic the grid ran. That is the
 * same discipline as AI-03, applied to the explanation surface.
 */

import type { CellExplanation } from '../api/client'
import { StatusChip } from './StatusChip'

export function ExplainPanel({ data, onClose }: { data: CellExplanation; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-label="How this value was obtained"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(11,11,11,.45)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: 'min(760px, 100%)', maxHeight: '85vh', overflow: 'auto', padding: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <h2>How this value was obtained</h2>
          <span className="chip neutral">{data.bucket.label} · {data.bucket.date || 'n/a'}</span>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>Close</button>
        </div>

        <Block title="Days on hand" value={data.doh.value.toFixed(1)} rule={data.doh.rule}>
          <StatusChip status={data.doh.status} label={`corridor ${data.doh.min_doh}–${data.doh.max_doh} days`} />
          {data.doh.horizon_edge && (
            <span className="chip neutral">horizon edge — no demand left to cover</span>
          )}
        </Block>

        <Block title="Active inventory" value={Math.round(data.inventory.value).toLocaleString()} rule={data.inventory.rule}>
          <Term label="previous" value={Math.round(data.inventory.previous).toLocaleString()} />
          <Term label="+ production" value={Math.round(data.inventory.production).toLocaleString()} />
          <Term label="− demand" value={Math.round(data.inventory.demand).toLocaleString()} />
        </Block>

        <Block
          title="Demonstrated capacity"
          value={data.demonstrated_capacity.value.toFixed(2)}
          rule={data.demonstrated_capacity.rule}
        >
          <Term label="open orders" value={data.demonstrated_capacity.open_orders.toFixed(2)} />
          <Term label="net balance" value={data.demonstrated_capacity.net_balance.toFixed(2)} />
          <span className="small muted">both in shift-equivalents, not pieces</span>
        </Block>

        <h3 style={{ margin: '18px 0 8px' }}>Per crew</h3>
        <table className="small mono" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Crew', 'Days', 'Install cap.', 'Crew %', 'Efficiency', 'Output', 'Pieces'].map((h) => (
                <th key={h} style={{ textAlign: h === 'Crew' ? 'left' : 'right', padding: '6px 8px', borderBottom: '1px solid var(--border-strong)', fontSize: 11 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.crews.map((c) => (
              <tr key={c.crew}>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--border)', fontFamily: 'Inter, sans-serif' }}>{c.crew}</td>
                <td style={numCell}>{c.days}</td>
                <td style={numCell}>{Math.round(c.installed_capacity).toLocaleString()}</td>
                <td style={numCell}>{c.crew_pct.toFixed(2)}</td>
                <td style={numCell}>{c.efficiency.toFixed(3)}</td>
                <td style={numCell}>{Math.round(c.output).toLocaleString()}</td>
                <td style={numCell}>{Math.round(c.pieces).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.crews[0] && <p className="small muted" style={{ marginTop: 8 }}>{data.crews[0].rule}</p>}
      </div>
    </div>
  )
}

const numCell: React.CSSProperties = {
  padding: '5px 8px', borderBottom: '1px solid var(--border)', textAlign: 'right',
}

function Block({ title, value, rule, children }: { title: string; value: string; rule: string; children?: React.ReactNode }) {
  return (
    <div style={{ padding: '12px 0', borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h3>{title}</h3>
        <span className="mono" style={{ fontSize: 20, fontWeight: 600 }}>{value}</span>
      </div>
      <p className="small muted" style={{ margin: '4px 0 8px' }}>{rule}</p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>{children}</div>
    </div>
  )
}

function Term({ label, value }: { label: string; value: string }) {
  return (
    <span className="chip neutral">
      {label} <strong className="mono">{value}</strong>
    </span>
  )
}
